import type {
  CarryStart,
  LineKey,
  LineNamesDraft,
  LineNamesDraftSource,
  NamedPlotRef,
  NameRow,
  SidRow,
  BreederGenealogyApplicationRegisterWritten as Written,
} from "@api/generated/model";
import {
  registerCarryPreview,
  registerNamesPreview,
  registerSequencePreview,
  registerSidPreview,
  useRegisterCarryRun,
  useRegisterDisplayPreview,
  useRegisterDisplayRun,
  useRegisterNamesRun,
  useRegisterSequenceRun,
  useRegisterSidRun,
} from "@api/generated/register/register";
import { useUserMe } from "@api/generated/user/user";
import { usePreview, useRunResult } from "@app/preview";
import { useTrial } from "@app/trials";
import { hasRole, useAutosave, useSaveSetting, useSeasonFromLink, useSettings } from "@app/workspace";
import { keyColumn } from "@features/genealogy/GenotypeParts";
import { keyText, lineTitle } from "@features/genealogy/genotype";
import { SavedQuerySelect, useSavedQuery } from "@features/queries/SavedQuerySelect";
import { ResultBanner } from "@features/system/undo";
import { hint } from "@i18n";
import { AnimatedTabsList } from "@ui/AnimatedTabsList";
import { QueryFeedback } from "@ui/QueryFeedback";
import "@features/genealogy/genealogy.css";
import { Button, Checkbox, Input, Loader, NumberInput, SegmentedControl, Select, Tabs, TagsInput, TextInput } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { formatNumber, num } from "@ui/format";
import { PageHeader } from "@ui/PageHeader";
import { flipped } from "@ui/picks";
import { rowToggle } from "@ui/rowToggle";
import { codeColumn } from "@ui/table/columns";
import { type Column, DataTable, useDataTable } from "@ui/table/DataTable";
import { type ReactNode, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

const TABS = ["newSid", "later", "names", "sequence", "displayNames"] as const;
type Tab = (typeof TABS)[number];

export const Route = createFileRoute("/genealogy/new-sid")({
  validateSearch: z.object({ tab: z.enum(TABS).optional(), ev: z.number().int().optional(), kis: z.string().optional() }),
  component: LinesPage,
});

/** Genealógia → SID és vonalnevek (the legacy New SID, NewSidSpecial, Create new line names, Change Pedigree
 *  Sequence and Update PedAkt): every change previewed first, then one undoable operation. */
function LinesPage() {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const roles = useUserMe().data?.roles;
  const ev = useSeasonFromLink(search.ev);
  const tab = search.tab ?? "newSid";
  if (!hasRole(roles, "nemesito")) {
    return (
      <div className="page">
        <PageHeader title={t("newSid.title")} />
        <p className="mu">{t("newSid.breederOnly")}</p>
      </div>
    );
  }
  if (!ev) return <Loader size="sm" m="md" />;
  const kis = search.kis ?? "";
  const setKis = (v: string | null) => void navigate({ search: (s) => ({ ...s, kis: v ?? undefined }) });
  return (
    <div className="page fill">
      <PageHeader
        title={t("newSid.title")}
        season
        subtitle={t("newSid.subtitle")}
        actions={<span className="sm mu">{t("newSid.permission")}</span>}
      />
      <Tabs value={tab} onChange={(v) => v && void navigate({ search: (s) => ({ ...s, tab: v as Tab }) })} keepMounted={false}>
        <AnimatedTabsList mb="md">
          {/* the administrators' tab only for them (a tab that only says "no access" is a dead end) */}
          {TABS.filter((x) => x !== "sequence" || hasRole(roles, "admin")).map((x) => (
            <Tabs.Tab key={x} value={x}>
              {t(`newSid.tab.${x}`)}
            </Tabs.Tab>
          ))}
        </AnimatedTabsList>
        <Tabs.Panel value="newSid">
          <NewSid ev={ev} kis={kis} onKis={setKis} />
        </Tabs.Panel>
        <Tabs.Panel value="later">
          <Carry ev={ev} kis={kis} onKis={setKis} />
        </Tabs.Panel>
        <Tabs.Panel value="names">
          <Names ev={ev} kis={kis} onKis={setKis} />
        </Tabs.Panel>
        <Tabs.Panel value="sequence">{hasRole(roles, "admin") ? <Sequence /> : <p className="mu">{t("newSid.adminOnly")}</p>}</Tabs.Panel>
        <Tabs.Panel value="displayNames">
          <DisplayNames />
        </Tabs.Panel>
      </Tabs>
    </div>
  );
}

function TrialSelect({ ev, kis, onKis }: { ev: number; kis: string; onKis: (v: string | null) => void }) {
  const { t } = useTranslation();
  const { trials } = useTrial(ev, undefined, undefined);
  return (
    <Select
      placeholder={hint("searchSelect")}
      size="xs"
      label={t("newSid.trial")}
      searchable
      value={kis || null}
      onChange={onKis}
      data={trials.map((o) => ({
        value: o.kis,
        label: `${ev} · ${o.kis} · ${t("value.plotCount", { count: formatNumber(o.register_plots) })}`,
      }))}
    />
  );
}

/** A run's result banner (``message``: its i18n key, with the rows written as ``db``) — while the request it ran
 *  with is still the one on the page — and the preview asked for again afterwards. */
function useDone(key: string, request: unknown, message: string, onUndone?: () => void) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const { result: out, keep } = useRunResult<Written>(request);
  return {
    banner: out && (
      <ResultBanner operation={out.operation} onUndone={onUndone}>
        {t(message, { count: out.written })}
      </ResultBanner>
    ),
    mutation: {
      onSuccess: (out: Written) => {
        keep(out);
        void client.invalidateQueries({ queryKey: [key] });
      },
    },
  };
}

/** The rule on the left, the preview on the right: the last run's result, its head (what it will do, the run
 *  button), then its table. */
function Layout(props: {
  rule: ReactNode;
  done?: ReactNode;
  chips?: ReactNode;
  extra?: ReactNode;
  run: ReactNode;
  children: ReactNode;
  foot?: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <div className="sv-grid">
      <section className="card pad stack" style={{ gap: 12 }}>
        {props.rule}
      </section>
      <section className="card stack" style={{ gap: 0, minWidth: 0 }}>
        {props.done}
        <div className="hstack panel-head">
          <h2 className="h2">{t("newSid.preview")}</h2>
          {props.chips}
          <span className="grow" />
          {props.extra}
          {props.run}
        </div>
        {props.children}
        {props.foot}
      </section>
    </div>
  );
}

/** A display name as the exceptions compare it (the server's rule: trimmed, case-insensitive). */
const nameKey = (name: string | null | undefined) => (name ?? "").trim().toUpperCase();

/** Names typed into a tag list (Enter adds one, a whole name: no splitting at commas; a name already listed, in any
 *  case, is not added twice). */
function NameChips(props: { label: string; names: string[]; onChange: (names: string[]) => void }) {
  return (
    <TagsInput
      size="xs"
      label={props.label}
      placeholder={hint("tags")}
      value={props.names}
      onChange={props.onChange}
      isDuplicate={(v, current) => current.some((n) => nameKey(n) === nameKey(v))}
      splitChars={[]}
      acceptValueOnBlur={false}
    />
  );
}

// --- Új SID ------------------------------------------------------------------------------------------------------------
/** A plot the rule gives a new line, unless its name is left out (``names``: the left-out names' ``nameKey``s). */
const sidOpen = (r: SidRow, names: Set<string>) => r.reason === "new" && !names.has(nameKey(r.name));

function NewSid({ ev, kis, onKis }: { ev: number; kis: string; onKis: (v: string | null) => void }) {
  const { t } = useTranslation();
  // every plot the rule gives a new line is ticked; the unticked ones are sent as the exceptions, and so are the names
  // whose every plot is left out (the legacy exception grid: one check variety's row excluded all its plots)
  const [skipped, setSkipped] = useState<Set<number>>(new Set());
  const [skipNames, setSkipNames] = useState<string[]>([]);
  const [filter, setFilter] = useState("");
  const [keep, setKeep] = useState(true);
  const body = kis ? { ev, kis, keep } : null; // which plots are ticked does not change the plan: the run takes them
  const preview = usePreview("sid-preview", body, (b, signal) => registerSidPreview(b, { signal }));
  const plan = preview.data;
  const { banner, mutation } = useDone("sid-preview", body, "newSid.newSidDone", () => setSkipped(new Set()));
  const run = useRegisterSidRun({ mutation });
  const names = useMemo(() => new Set(skipNames.map(nameKey)), [skipNames]);
  const eligible = (plan?.rows ?? []).filter((r) => sidOpen(r, names));
  const count = eligible.filter((r) => !skipped.has(r.parc)).length;
  const f = filter.trim().toUpperCase();
  const shown = useMemo(() => (plan?.rows ?? []).filter((r) => !f || `${r.parc} ${r.name ?? ""}`.toUpperCase().includes(f)), [plan, f]);
  const toggle = useCallback(
    (parc: number) =>
      setSkipped((l) => {
        const next = new Set(l);
        if (!next.delete(parc)) next.add(parc);
        return next;
      }),
    [],
  );
  const columns = useMemo<Column<SidRow>[]>(
    () => [
      {
        id: "pick",
        // every new SID of the rows shown on or off
        header: (rows) => {
          const visible = rows.filter((r) => sidOpen(r, names));
          const on = visible.filter((r) => !skipped.has(r.parc)).length;
          const all = visible.length > 0 && on === visible.length;
          return (
            <Checkbox
              size="xs"
              aria-label={t("newSid.toggleAll")}
              disabled={!visible.length}
              checked={all}
              indeterminate={on > 0 && !all}
              onChange={() =>
                setSkipped((l) => {
                  const next = new Set(l);
                  for (const r of visible) {
                    if (all) next.add(r.parc);
                    else next.delete(r.parc);
                  }
                  return next;
                })
              }
            />
          );
        },
        fixed: true,
        cell: (r) => (
          <Checkbox
            size="xs"
            aria-label={t("newSid.plotToggle", { parc: r.parc })}
            disabled={!sidOpen(r, names)}
            checked={sidOpen(r, names) && !skipped.has(r.parc)}
            onChange={() => toggle(r.parc)}
          />
        ),
      },
      codeColumn("plot", t("newSid.plot"), (r) => r.parc),
      keyColumn((r) => r.key),
      {
        id: "name",
        header: t("newSid.name"),
        value: (r) => r.name,
        className: "mono sm",
        cell: (r) => (
          <>
            {r.name}{" "}
            {r.name && sidOpen(r, names) && (
              <Button
                size="compact-xs"
                variant="subtle"
                title={t("newSid.skipNameLabel", { name: r.name })}
                aria-label={t("newSid.skipNameLabel", { name: r.name })}
                onClick={() => setSkipNames([...skipNames, (r.name ?? "").trim()])}
              >
                {t("newSid.skipName")}
              </Button>
            )}
          </>
        ),
      },
      {
        id: "result",
        header: t("newSid.result"),
        // what the rule says (not the tick: a row would move or vanish under a sort or filter when it is ticked)
        value: (r) => t(`newSid.reason.${r.reason}`),
        text: (r) => `${t(`newSid.reason.${r.reason}`)} ${r.reason === "new" ? r.torzs_szam : ""}`,
        className: "sm",
        cell: (r) => {
          const reason = sidOpen(r, names) && !skipped.has(r.parc) ? "new" : r.reason === "new" ? "excluded" : r.reason;
          return (
            <>
              <span className={`chip ${reason === "new" ? "g" : ""}`}>{t(`newSid.reason.${reason}`)}</span>{" "}
              {reason === "new" && <span className="mono mu">{r.torzs_szam}</span>}
            </>
          );
        },
      },
    ],
    [t, names, skipped, skipNames, toggle],
  );
  const table = useDataTable("genealogy.new-sid.plots", columns, plan && shown);
  return (
    <Layout
      rule={
        <>
          <QueryFeedback busy={preview.busy} error={preview.error} retry={preview.refetch} label={t("async.preview")} />
          <TrialSelect ev={ev} kis={kis} onKis={onKis} />
          <span className="sm mu">{t("newSid.selectionHelp")}</span>
          <Checkbox
            size="xs"
            label={t("newSid.keepContinued")}
            description={t("newSid.keepContinuedHelp")}
            checked={keep}
            onChange={(e) => setKeep(e.currentTarget.checked)}
          />
          <NameChips label={t("newSid.skippedNames")} names={skipNames} onChange={setSkipNames} />
          <span className="sm mu">{t("newSid.skippedNamesHelp")}</span>
        </>
      }
      done={banner}
      chips={plan && <span className="chip g">{t("newSid.newSidCount", { count: count, total: eligible.length })}</span>}
      extra={
        <TextInput
          size="xs"
          w={180}
          placeholder={t("newSid.filter")}
          aria-label={t("newSid.filter")}
          value={filter}
          onChange={(e) => setFilter(e.currentTarget.value)}
        />
      }
      run={
        <Button
          disabled={!count || !body}
          loading={run.isPending}
          onClick={() => body && plan && run.mutate({ data: { ...body, skip: [...skipped], skip_names: skipNames, token: plan.token } })}
        >
          {t("newSid.newSidRun", { count: count })}
        </Button>
      }
    >
      <DataTable
        table={table}
        rowKey={(r) => r.parc}
        rowProps={(r) =>
          rowToggle({ selected: sidOpen(r, names) && !skipped.has(r.parc), onToggle: () => toggle(r.parc), enabled: sidOpen(r, names) })
        }
      />
    </Layout>
  );
}

// --- Új SID a későbbi évekre is ------------------------------------------------------------------------------------------
const plotText = ([y, k, p]: [number, string, number]) => `${y} ${k} ${p}`;

/** Új SID a későbbi évekre is (the legacy NewSidSpecial): plots of the trial clicked into the list, each gets a new
 *  SID of its own carried along the later years' plots sown from it; the trials the walk does not go through are the
 *  user's setting (the legacy list by default). */
function Carry({ ev, kis, onKis }: { ev: number; kis: string; onKis: (v: string | null) => void }) {
  const { t } = useTranslation();
  const settings = useSettings();
  const saveSetting = useSaveSetting();
  const [parcs, setParcs] = useState<number[]>([]);
  const [filter, setFilter] = useState("");
  const exclude = settings?.carry_excluded_trials;
  // the trial's plots with their lines to pick from (New SID's plan lists them all)
  const trial = usePreview("carry-plots", kis ? { ev, kis, keep: false } : null, (b, signal) => registerSidPreview(b, { signal }));
  const body = kis && parcs.length && exclude ? { ev, kis, parcs, exclude } : null;
  const preview = usePreview("carry-preview", body, (b, signal) => registerCarryPreview(b, { signal }));
  const plan = preview.data;
  const { banner, mutation } = useDone("carry-preview", body, "newSid.laterDone", () => setParcs([]));
  const run = useRegisterCarryRun({ mutation });
  const picked = useMemo(() => new Set(parcs), [parcs]);
  const toggle = useCallback((parc: number) => setParcs((l) => flipped(l, parc)), []);
  const f = filter.trim().toUpperCase();
  const plots = useMemo(() => trial.data?.rows.filter((r) => !f || `${r.parc} ${r.name ?? ""}`.toUpperCase().includes(f)), [trial.data, f]);
  const later = (plan?.starts ?? []).reduce((n, s) => n + s.plots.length, 0);
  const plotColumns = useMemo<Column<SidRow>[]>(
    () => [
      {
        id: "pick",
        header: "",
        fixed: true,
        cell: (r) => (
          <Checkbox
            size="xs"
            aria-label={t("newSid.laterSelect", { parc: r.parc })}
            checked={picked.has(r.parc)}
            onChange={() => toggle(r.parc)}
          />
        ),
      },
      codeColumn("plot", t("newSid.plot"), (r) => r.parc),
      { id: "name", header: t("newSid.name"), value: (r) => lineTitle(r), className: "mono sm" },
    ],
    [t, picked, toggle],
  );
  const plotTable = useDataTable("genealogy.new-sid.later.plots", plotColumns, plots);
  const columns = useMemo<Column<CarryStart>[]>(() => {
    const list = (id: "plots" | "excluded"): Column<CarryStart> => ({
      id,
      header: t(id === "plots" ? "newSid.laterPlots" : "newSid.skipped"),
      value: (s) => s[id].map(plotText).join(", "),
      className: id === "plots" ? "mono sm wrap" : "mono sm wrap mu",
    });
    return [
      codeColumn("plot", t("newSid.plot"), (s) => s.parc),
      keyColumn((s) => s.key),
      // the new numbers are coloured, not their heading
      { id: "number", header: t("newSid.newLineNumber"), value: (s) => s.number, className: "mono", cellClassName: () => "sv-new" },
      list("plots"),
      list("excluded"),
      { id: "genebank", header: t("newSid.genebank"), value: (s) => s.accessions, numeric: true },
      { id: "dna", header: t("newSid.dna"), value: (s) => s.samples, numeric: true },
      { id: "shipments", header: t("newSid.seedShipment"), value: (s) => s.shipments, numeric: true },
    ];
  }, [t]);
  const table = useDataTable("genealogy.new-sid.later.preview", columns, plan?.starts);
  return (
    <Layout
      rule={
        <>
          <QueryFeedback busy={preview.busy} error={preview.error} retry={preview.refetch} label={t("async.preview")} />
          <TrialSelect
            ev={ev}
            kis={kis}
            onKis={(v) => {
              setParcs([]);
              onKis(v);
            }}
          />
          <span className="sm mu">{t("newSid.laterHelp")}</span>
          {kis && (
            <>
              <TextInput
                size="xs"
                placeholder={t("newSid.filter")}
                aria-label={t("newSid.filter")}
                value={filter}
                onChange={(e) => setFilter(e.currentTarget.value)}
              />
              <div className="stack" style={{ maxHeight: 260 }}>
                <DataTable
                  table={plotTable}
                  rowKey={(r) => r.parc}
                  rowProps={(r) => rowToggle({ selected: picked.has(r.parc), onToggle: () => toggle(r.parc) })}
                />
              </div>
            </>
          )}
          {exclude && (
            <NameChips label={t("newSid.excludedTrials")} names={exclude} onChange={(next) => saveSetting("carry_excluded_trials", next)} />
          )}
          <span className="sm mu">{t("newSid.excludedTrialsHelp")}</span>
        </>
      }
      done={banner}
      chips={
        plan && (
          <>
            <span className="chip g">{t("newSid.laterCount", { count: plan.starts.length, later: later })}</span>
            {plan.overlap.length > 0 && <span className="chip r">{t("newSid.overlap", { count: plan.overlap.length })}</span>}
          </>
        )
      }
      run={
        <Button
          disabled={!plan || !body || plan.overlap.length > 0}
          loading={run.isPending}
          onClick={() => body && plan && run.mutate({ data: { ...body, token: plan.token } })}
        >
          {t("newSid.laterRun", { count: plan?.starts.length ?? 0 })}
        </Button>
      }
      foot={
        (preview.isError && body && <p className="note warn sm inset">{t("newSid.noPlots")}</p>) ||
        (plan && plan.overlap.length > 0 && (
          <p className="note warn sm inset">{t("newSid.overlapHelp", { plots: plan.overlap.slice(0, 8).map(plotText).join(", ") })}</p>
        ))
      }
    >
      <DataTable table={table} rowKey={(s) => s.parc} />
    </Layout>
  );
}

// --- Új vonalnevek -------------------------------------------------------------------------------------------------------
function Names(props: { ev: number; kis: string; onKis: (v: string | null) => void }) {
  const settings = useSettings();
  if (!settings) return <Loader size="sm" m="md" />;
  return <NamesForm {...props} stored={settings.line_names_draft ?? null} />;
}

const plotKey = (kis: string, parc: number) => `${kis}\u0000${parc}`;

/** New line names for a trial's plots (plot order) or a saved query's plots of the season (the query's order: the
 *  legacy Query closed on Create new line names). The source and the rule are the user's draft (``line_names_draft``);
 *  the trial is in the link, shared with the other tabs. */
function NamesForm({
  ev,
  kis,
  onKis,
  stored,
}: {
  ev: number;
  kis: string;
  onKis: (v: string | null) => void;
  stored: LineNamesDraft | null;
}) {
  const { t } = useTranslation();
  const [init] = useState(() => stored);
  const [source, setSource] = useState<LineNamesDraftSource>(init?.source ?? "trial");
  const [query, setQuery] = useState<number | null>(init?.query ?? null);
  const [prefix, setPrefix] = useState(init?.prefix ?? "MV");
  const [start, setStart] = useState(init?.start ?? "04");
  const [year, setYear] = useState<number | null>(init?.year ?? null); // empty: the trial's year
  const [skip, setSkip] = useState<string[]>(init?.skip ?? []);
  const [onlyUnnamed, setOnlyUnnamed] = useState(init?.only_unnamed ?? true);
  const [renameIn, setRenameIn] = useState(init?.rename_in_pedigrees ?? true);
  const draft = useMemo<LineNamesDraft>(
    () => ({ source, query, prefix, start, year, skip, only_unnamed: onlyUnnamed, rename_in_pedigrees: renameIn }),
    [source, query, prefix, start, year, skip, onlyUnnamed, renameIn],
  );
  useAutosave("line_names_draft", draft, init);
  const [filter, setFilter] = useState("");
  // the plots unticked in the preview: listed, not named, the numbers run on without them (the legacy named a subset
  // by clicking its rows); they are part of the request, so the plan and its fingerprint follow them
  const [unticked, setUnticked] = useState<NamedPlotRef[]>([]);
  const byQuery = source === "query";
  // the query's updated_at in the preview's key: saving the query in the builder asks again
  const saved = useSavedQuery(query, byQuery);
  const rule = { ev, prefix, start, year: year ?? 0, skip, only_unnamed: onlyUnnamed, rename_in_pedigrees: renameIn };
  const ready = start && (byQuery ? query != null && saved.ready : !!kis);
  const body = ready ? { ...rule, ...(byQuery ? { query } : { kis }), skip_trial_plots: unticked } : null;
  const preview = usePreview("names-preview", body && { body, queryStamp: saved.stamp }, (b, signal) =>
    registerNamesPreview(b.body, { signal }),
  );
  const plan = preview.data;
  const { banner, mutation } = useDone("names-preview", body, "newSid.namesDone", () => setUnticked([]));
  const run = useRegisterNamesRun({ mutation });
  const f = filter.trim().toUpperCase();
  const rows = useMemo(
    () => plan?.rows.filter((r) => !f || `${r.kis} ${r.parc} ${r.name ?? ""} ${r.new_name ?? ""}`.toUpperCase().includes(f)),
    [plan, f],
  );
  const named = (plan?.rows ?? []).filter((r) => r.new_name);
  const sample = named.slice(0, 2).map((r) => r.new_name);
  const off = useMemo(() => new Set(unticked.map((p) => plotKey(p.kis, p.parc))), [unticked]);
  const toggle = useCallback(
    (p: NamedPlotRef) =>
      setUnticked((l) =>
        l.some((x) => plotKey(x.kis, x.parc) === plotKey(p.kis, p.parc))
          ? l.filter((x) => plotKey(x.kis, x.parc) !== plotKey(p.kis, p.parc))
          : [...l, p],
      ),
    [],
  );
  // the heading's tick box: the plots shown on or off (the others keep their ticks)
  const setShown = (plots: NamedPlotRef[], on: boolean) => {
    const keys = new Set(plots.map((p) => plotKey(p.kis, p.parc)));
    setUnticked((l) => [...l.filter((x) => !keys.has(plotKey(x.kis, x.parc))), ...(on ? [] : plots)]);
  };
  const changeSource = (v: LineNamesDraftSource) => {
    setSource(v);
    setUnticked([]);
  };
  return (
    <Layout
      rule={
        <>
          <QueryFeedback busy={preview.busy} error={preview.error} retry={preview.refetch} label={t("async.preview")} />
          <h2 className="h2">{t("newSid.namingRule")}</h2>
          <Input.Wrapper size="xs" label={t("newSid.nameSource")}>
            <SegmentedControl
              size="xs"
              fullWidth
              value={source}
              onChange={(v) => changeSource(v as LineNamesDraftSource)}
              data={(["trial", "query"] as const).map((s) => ({ value: s, label: t(`newSid.nameSourceMode.${s}`) }))}
            />
          </Input.Wrapper>
          {byQuery ? (
            <SavedQuerySelect
              value={query}
              onChange={(id) => {
                setQuery(id);
                setUnticked([]);
              }}
              w="100%"
              required
              description={t("newSid.nameQueryHelp", { ev })}
            />
          ) : (
            <TrialSelect
              ev={ev}
              kis={kis}
              onKis={(v) => {
                setUnticked([]);
                onKis(v);
              }}
            />
          )}
          {byQuery && plan?.query_rows != null && (
            <p className="sm mu">
              {plan.rows.length
                ? t("newSid.nameQueryResult", {
                    rows: formatNumber(plan.query_rows),
                    count: formatNumber(plan.query_plots ?? 0),
                    lines: formatNumber(plan.rows.length),
                  })
                : t("newSid.nameQueryEmpty")}
            </p>
          )}
          <div className="sv-grid3">
            <TextInput size="xs" label={t("newSid.prefix")} value={prefix} onChange={(e) => setPrefix(e.currentTarget.value)} />
            <TextInput
              size="xs"
              label={t("newSid.startNumber")}
              value={start}
              onChange={(e) => setStart(e.currentTarget.value.replace(/\D/g, ""))}
            />
            <NumberInput
              size="xs"
              label={t("newSid.yearTwoDigit")}
              placeholder={String(ev % 100)}
              min={0}
              max={99}
              allowDecimal={false}
              value={year ?? ""}
              onChange={(v) => setYear(num(v))}
            />
          </div>
          {sample.length > 0 && (
            <div>
              <span className="sec">{t("newSid.example")}</span>
              <div className="mono">{sample.join(", ")} …</div>
            </div>
          )}
          <NameChips label={t("newSid.skippedChecks")} names={skip} onChange={setSkip} />
          <Checkbox
            size="xs"
            label={t("newSid.unnamedOnly")}
            checked={onlyUnnamed}
            onChange={(e) => setOnlyUnnamed(e.currentTarget.checked)}
          />
          <Checkbox
            size="xs"
            label={t("newSid.replaceInPedigrees")}
            checked={renameIn}
            onChange={(e) => setRenameIn(e.currentTarget.checked)}
          />
        </>
      }
      done={banner}
      chips={
        plan && (
          <>
            <span className="chip g">{t("newSid.newNameCount", { count: named.length })}</span>
            {plan.pedigrees > 0 && <span className="chip y">{t("newSid.referenceCount", { count: plan.pedigrees })}</span>}
          </>
        )
      }
      extra={
        <TextInput
          size="xs"
          w={180}
          placeholder={t("newSid.filter")}
          aria-label={t("newSid.filter")}
          value={filter}
          onChange={(e) => setFilter(e.currentTarget.value)}
        />
      }
      run={
        <Button
          disabled={!named.length || !body || plan?.rows.some((r) => r.taken)}
          loading={run.isPending}
          onClick={() => body && plan && run.mutate({ data: { ...body, token: plan.token } })}
        >
          {t("newSid.saveNames", { count: named.length })}
        </Button>
      }
      foot={<p className="sm mu inset">{t("newSid.replaceHelp")}</p>}
    >
      <NameTable rows={rows} trials={byQuery} unticked={off} onToggle={toggle} onShown={setShown} />
    </Layout>
  );
}

function NameTable(props: {
  rows: NameRow[] | undefined;
  trials: boolean; // the rows come from several trials (a saved query): show each one's trial
  unticked: Set<string>;
  onToggle: (plot: NamedPlotRef) => void;
  onShown: (plots: NamedPlotRef[], on: boolean) => void;
}) {
  const { t } = useTranslation();
  const { trials, unticked, onToggle, onShown } = props;
  const columns = useMemo<Column<NameRow>[]>(
    () => [
      {
        id: "pick",
        // every plot shown on or off
        header: (rows) => {
          const shown = rows.map((r) => ({ kis: r.kis, parc: r.parc }));
          const on = shown.filter((p) => !unticked.has(plotKey(p.kis, p.parc))).length;
          const all = shown.length > 0 && on === shown.length;
          return (
            <Checkbox
              size="xs"
              aria-label={t("newSid.namesToggleAll")}
              disabled={!shown.length}
              checked={all}
              indeterminate={on > 0 && !all}
              onChange={() => onShown(shown, !all)}
            />
          );
        },
        fixed: true,
        cell: (r) => (
          <Checkbox
            size="xs"
            aria-label={t("newSid.nameToggle", { parc: trials ? `${r.kis} ${r.parc}` : r.parc })}
            checked={!unticked.has(plotKey(r.kis, r.parc))}
            onChange={() => onToggle({ kis: r.kis, parc: r.parc })}
          />
        ),
      },
      ...(trials ? [{ id: "trial", header: t("newSid.trial"), value: (r: NameRow) => r.kis, className: "mono" }] : []),
      codeColumn("plot", t("newSid.plot"), (r) => r.parc),
      keyColumn((r) => r.key),
      { id: "name", header: t("newSid.currentName"), value: (r) => r.name, className: "mono mu" },
      {
        id: "new_name",
        header: t("newSid.newLineName"),
        value: (r) => r.new_name,
        text: (r) => (r.new_name ? `${r.new_name} ${r.taken ? t("newSid.taken") : ""}` : t("newSid.noName")),
        className: "mono",
        cell: (r) => (
          <>
            {r.new_name ? (
              <span className={r.taken ? "chip r" : "sv-new"}>{r.new_name}</span>
            ) : (
              <span className="mu">{t("newSid.noName")}</span>
            )}
            {r.taken && <span className="sm"> {t("newSid.taken")}</span>}
          </>
        ),
      },
      {
        id: "pedigrees",
        header: t("newSid.referencingPedigrees"),
        value: (r) => r.pedigrees.map(([, old, now]) => `${old} → ${now}`).join("; "),
        className: "mono sm wrap",
        cell: (r) =>
          r.pedigrees.length ? (
            r.pedigrees.map(([paz, old, now]) => <div key={paz}>{`${old} → ${now}`}</div>)
          ) : (
            <span className="mu">—</span>
          ),
      },
    ],
    [t, trials, unticked, onToggle, onShown],
  );
  const table = useDataTable("genealogy.new-sid.names", columns, props.rows);
  return (
    <DataTable
      table={table}
      rowKey={(r) => keyText(r.key)}
      rowProps={(r) =>
        rowToggle({ selected: !unticked.has(plotKey(r.kis, r.parc)), onToggle: () => onToggle({ kis: r.kis, parc: r.parc }) })
      }
    />
  );
}

// --- Pedigré-szekvencia csere (admin) --------------------------------------------------------------------------------
function Sequence() {
  const { t } = useTranslation();
  const [old, setOld] = useState("");
  const [now, setNow] = useState("");
  const [pgm, setPgm] = useState("");
  const [gh, setGh] = useState("");
  const body = old.trim() && now.trim() ? { old: old.trim(), new: now.trim(), pgm: pgm.trim() || null, gh: gh.trim() || null } : null;
  const preview = usePreview("sequence-preview", body, (b, signal) => registerSequencePreview(b, { signal }));
  const plan = preview.data;
  const { banner, mutation } = useDone("sequence-preview", body, "newSid.replaceDone");
  const run = useRegisterSequenceRun({ mutation });
  const columns = useMemo<Column<[number, string, string]>[]>(
    () => [
      { id: "paz", header: "PAz", value: (r) => r[0], text: (r) => String(r[0]), className: "mono" },
      { id: "old", header: t("newSid.oldPedigree"), value: (r) => r[1], className: "mono sm" },
      { id: "new", header: t("newSid.newPedigree"), value: (r) => r[2], className: "mono sm" },
    ],
    [t],
  );
  const table = useDataTable("genealogy.new-sid.sequence", columns, plan?.rows);
  return (
    <Layout
      rule={
        <>
          <QueryFeedback busy={preview.busy} error={preview.error} retry={preview.refetch} label={t("async.preview")} />
          <h2 className="h2">{t("newSid.tab.sequence")}</h2>
          <TextInput
            size="xs"
            label={t("newSid.oldParent")}
            description={t("newSid.oldParentHelp")}
            value={old}
            onChange={(e) => setOld(e.currentTarget.value)}
          />
          <TextInput size="xs" label={t("newSid.newParent")} value={now} onChange={(e) => setNow(e.currentTarget.value)} />
          <div className="sv-grid3">
            <TextInput size="xs" label="PGM" value={pgm} onChange={(e) => setPgm(e.currentTarget.value)} />
            <TextInput size="xs" label="GH" value={gh} onChange={(e) => setGh(e.currentTarget.value)} />
          </div>
        </>
      }
      done={banner}
      chips={plan && <span className="chip y">{t("newSid.replaceCount", { count: plan.rows.length })}</span>}
      run={
        <Button
          disabled={!plan?.rows.length || !body}
          loading={run.isPending}
          onClick={() => body && plan && run.mutate({ data: { ...body, token: plan.token } })}
        >
          {t("newSid.replaceRun")}
        </Button>
      }
      foot={preview.isError && body && <p className="note warn sm inset">{t("newSid.replaceError")}</p>}
    >
      <DataTable table={table} rowKey={(r) => r[0]} />
    </Layout>
  );
}

// --- Megjelenített nevek frissítése --------------------------------------------------------------------------------------
function DisplayNames() {
  const { t } = useTranslation();
  const plan = useRegisterDisplayPreview().data;
  const { banner, mutation } = useDone("/api/v1/lines/display-names", null, "newSid.refreshDone");
  const run = useRegisterDisplayRun({ mutation });
  const columns = useMemo<Column<[LineKey, string | null, string | null]>[]>(
    () => [
      keyColumn((r) => r[0]),
      { id: "stored", header: t("newSid.storedName"), value: (r) => r[1], className: "mono sm mu" },
      { id: "rule", header: t("newSid.byRule"), value: (r) => r[2], className: "mono sm" },
    ],
    [t],
  );
  // the server lists the first changes of many (the run takes them all)
  const table = useDataTable("genealogy.new-sid.display-names", columns, plan?.rows, { partial: !!plan && plan.total > plan.rows.length });
  return (
    <section className="card stack" style={{ gap: 0 }}>
      {banner}
      <div className="hstack panel-head">
        <span className="sm mu grow">{t("newSid.refreshHelp")}</span>
        {plan && <span className="chip y">{t("newSid.refreshCount", { count: formatNumber(plan.total) })}</span>}
        <Button disabled={!plan?.total} loading={run.isPending} onClick={() => plan && run.mutate({ data: { token: plan.token } })}>
          {t("newSid.refreshRun")}
        </Button>
      </div>
      <DataTable table={table} rowKey={(r) => keyText(r[0])} />
    </section>
  );
}
